import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// The one modal shell. Every dialog in the app renders through this.
//
// Before it, 21 files hand-rolled the same two <div>s:
//
//   <div className="modal-overlay" onClick={onClose}>
//     <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
//
// which meant the BEHAVIOUR was per-file and mostly absent: one of them closed
// on Escape, none locked the page behind them, six declared a dialog role, one
// set aria-modal, and nothing anywhere trapped or restored focus — so Tab
// walked straight out of a modal into the page underneath it, and closing one
// dropped focus on <body>, sending a keyboard user back to the top of the page.
//
// It deliberately renders the SAME `.modal-overlay` / `.modal` markup rather
// than something new. Those classes carry a hard-won fix: wide tables
// intentionally overflow the document horizontally (see the .table-wrapper note
// in index.css), which on an RTL page widens the initial containing block, and
// `inset: 0` then resolves against the DOCUMENT rather than the viewport — at
// 390px the overlay came out 818px wide starting at -428 and every modal
// centred itself off-screen. The overlay's explicit 100vw/100dvh is what fixes
// that, so reuse the classes and inherit the fix.
//
// Not a native <dialog>: those render in the browser's top layer, above
// everything regardless of z-index, and the toast container sits at z-index
// 2000 in the normal layer. Every "המסמך נשמר" fired from inside a modal would
// paint BEHIND it. The app's z-index ladder is deliberate; a React portal
// respects it, the top layer does not.

// How many Modals are open. Module-level, and used for ONE thing: the body
// scroll lock is shared state, so only the outermost modal may set it and only
// the last one to close may restore it. A nested dialog closing while its
// parent is still open must not hand the page back its scrollbar.
//
// Deliberately NOT used to decide which modal Escape belongs to. 20 dialogs in
// this app still hand-roll `.modal-overlay` and are invisible to this counter,
// so a stack of Modals would answer "am I topmost?" wrongly the moment a
// hand-rolled one opens above a converted one. DOM order is the only thing that
// sees both, so that is what the Escape handler asks. Once every dialog is a
// Modal, this can become a real stack and the DOM query can go.
let openCount = 0;
// The page's own overflow, captured on the 0->1 edge and restored on 1->0.
// It MUST NOT be a per-instance closure: each modal would capture whatever the
// previous one had already set, so if the first-mounted modal unmounts first
// the last one restores 'hidden' and the page can never be scrolled again.
let prevBodyOverflow = '';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

const SIZES = { sm: 440, md: 500, lg: 720 };

// Everything that can sit above a dialog and owns its own Escape.
const DISMISSIBLE_LAYERS = [
  '.modal-overlay > .modal',
  '.filter-sheet',
  '.docs-filter-datepop',
  '.payment-sheet',
  // A scan opened full screen from inside a modal (the console's deleted-
  // document viewer): Escape leaves the scan first, the modal stays.
  '.dfv--fullscreen',
].join(',');

export default function Modal({
  open = true,
  onClose,
  title,
  // Screen-reader name. Falls back to `title`; pass this when the visible
  // title is a node rather than a string.
  ariaLabel,
  size = 'md',
  // 'alertdialog' for a confirm that interrupts to ask a question;
  // 'dialog' for everything else.
  role = 'dialog',
  // Forms holding unsaved input should pass false — losing a typed payment
  // amount to a stray click on the backdrop is worse than an extra click on
  // ביטול.
  closeOnBackdrop = true,
  // Optional ref to focus on open (e.g. the first text field). Without it,
  // focus goes to the dialog box itself, so a screen reader reads the title
  // before the controls.
  initialFocus,
  // Passed to that focus call: `{ preventScroll: true }` keeps the dialog at its
  // top when the focused control sits lower down.
  initialFocusOptions,
  className = '',
  style,
  children,
}) {
  const boxRef = useRef(null);
  const restoreRef = useRef(null);
  // Kept in a ref so the Escape listener never goes stale on a caller that
  // re-creates onClose every render (most of them do).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Read through a ref so a caller that swaps the ref object (ConfirmModal
  // does, keyed on tone) cannot re-run the focus effect — its cleanup
  // restores focus to the trigger, which would throw focus out of a dialog
  // that is still open.
  const initialFocusRef = useRef(initialFocus);
  initialFocusRef.current = initialFocus;
  const focusOptionsRef = useRef(initialFocusOptions);
  focusOptionsRef.current = initialFocusOptions;

  // Register in the stack + lock body scroll while any modal is open.
  useEffect(() => {
    if (!open) return undefined;
    // Only the outermost modal locks, and only the last one to close restores —
    // a nested dialog closing while its parent is still open must not hand the
    // page back its scrollbar.
    openCount += 1;
    if (openCount === 1) {
      prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    return () => {
      openCount -= 1;
      if (openCount === 0) document.body.style.overflow = prevBodyOverflow;
    };
  }, [open]);

  // Escape closes — but only the topmost modal.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || !boxRef.current) return;
      // Topmost wins, and "topmost" is measured in the DOM rather than against
      // a list of Modals: the layers above a modal are not all modals. A
      // PeriodPicker popover, a FilterSheet or a payment sheet can open ON TOP
      // of a dialog, each with its own Escape handler — and because those
      // handlers are on `document` too, stopPropagation cannot separate them
      // (only stopImmediatePropagation could, and the listener order is not
      // ours to rely on). So a dialog must decline the key whenever ANY
      // dismissible layer sits above it, or Escape aimed at the date picker
      // inside LinkDeliveryNotesModal would also close the modal and discard
      // the user's selection. All of these portal to <body>, so DOM order is
      // paint order.
      const layers = document.querySelectorAll(DISMISSIBLE_LAYERS);
      if (layers[layers.length - 1] !== boxRef.current) return;
      e.stopPropagation();
      closeRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Move focus in on open, and put it back where it came from on close.
  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;
    const target = initialFocusRef.current?.current
      || boxRef.current?.querySelector(FOCUSABLE)
      || boxRef.current;
    target?.focus?.(focusOptionsRef.current);
    return () => {
      const el = restoreRef.current;
      // The trigger can be gone (a row that the action deleted); focusing a
      // detached node throws nothing but silently drops focus on <body>.
      if (el && document.contains(el)) el.focus?.();
    };
  }, [open]);

  // Keep Tab inside the dialog.
  const onKeyDown = useCallback((e) => {
    if (e.key !== 'Tab' || !boxRef.current) return;
    // No offsetParent/visibility filter here on purpose: offsetParent is null
    // for everything under a position:fixed ancestor in some engines and null
    // for EVERYTHING in jsdom, which silently collapsed the cycle to one item.
    // FOCUSABLE already excludes disabled controls and tabindex="-1".
    const items = [...boxRef.current.querySelectorAll(FOCUSABLE)]
      .filter(el => !el.closest('[hidden]'));
    if (items.length === 0) { e.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, []);

  if (!open) return null;

  const maxWidth = SIZES[size] || size;

  return createPortal(
    <div
      className="modal-overlay"
      onClick={closeOnBackdrop ? () => closeRef.current?.() : undefined}
    >
      <div
        ref={boxRef}
        className={`modal${className ? ' ' + className : ''}`}
        // The box swallows clicks so a click INSIDE never reaches the backdrop
        // handler above. Nested dialogs no longer need their own copy of this.
        onClick={e => e.stopPropagation()}
        onKeyDown={onKeyDown}
        style={{ maxWidth, ...style }}
        role={role}
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : undefined)}
        tabIndex={-1}
      >
        {title && <h2 className="modal-title">{title}</h2>}
        {children}
      </div>
    </div>,
    document.body,
  );
}
